import useCarHireOfferImportantInformation from 'hooks/CarHire/useCarHireImportantInformation'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import LineCoinsIcon from 'LuxKit/Icons/line/LineCoinsIcon'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import React from 'react'
import CarHireImportantInformationList from '../CarHireOfferTile/CarHireImportantInformationList'
import Divider from 'LuxKit/Divider'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'

const HeadingBlock = styled(HeadingTextBlock)`
  .variant-heading6 {
    font-size: 14px;
  }
`

const CarInfoList = styled(CarHireImportantInformationList)`
  margin: 0 !important;
`

const BannerWrapper = styled.div`
  padding: ${rem(20)} 0;
`

interface Props {
  offer: App.CarHireOffer
  pickUpDate: string
  dropOffDate: string
  pickUpTime: string
  dropOffTime: string
  onClick: () => void
  hasEnhancedProtection: boolean
}

function CarHireInsuranceLimitedProtectionInfo(props: Props) {
  const {
    offer,
    pickUpDate,
    dropOffDate,
    pickUpTime,
    dropOffTime,
    onClick,
    hasEnhancedProtection,
  } = props

  const intl = useIntl()

  const whatsCoveredLabel = intl.formatMessage({
    id: 'KhGPCv8msL-yrq54f0BG',
    defaultMessage: 'What’s covered',
    description: 'Heading for the car hire limited protection coverage section',
  })

  const excessFeeLabel = intl.formatMessage({
    id: 'CCl-qvgSXWjio0VZajI4',
    defaultMessage: 'Excess Fee',
    description: 'Heading for the excess fee section in car hire limited protection',
  })

  const excessLiabilityText = intl.formatMessage({
    id: 'Td3RmMQ0-twNpejOSmnE',
    defaultMessage: 'In the case of damage to your rental car, you may be liable for the following excess:',
    description: 'Description text explaining excess liability for car hire limited protection',
  })

  const thirdPartyProtectionLabel = intl.formatMessage({
    id: 'A7EQW6G2xyhy96QNz9bg',
    defaultMessage: 'Third Party Protection',
    description: 'Heading for third party protection section in car hire limited protection',
  })

  const usThirdPartyText = intl.formatMessage({
    id: 'm-KsgeLjgXihOMZC4Bej',
    defaultMessage: 'Included as part of your rental agreement. If you or any authorised driver are involved in an accident, Third Party injury and property damage is already covered.',
    description: 'Third party protection description for US car hire destinations',
  })

  const allThirdPartyText = intl.formatMessage({
    id: 'MCns3HFmvuxoDt3mYmI-',
    defaultMessage: 'Included as part of your protection. If you or any authorised driver cause an accident, supplier covers injury or damage to property caused by your use of the car during the rental period. It doesn’t cover your injuries or damage to the rental car.',
    description: 'Third party protection description for non-US car hire destinations',
  })

  const usBannerText = intl.formatMessage({
    id: 'wN0IjGpR8Xu-mIMH3Cgp',
    defaultMessage: 'With Enhanced Protection, you’re covered for personal possessions, accidental damages, misfuelling, and more. You can select your insurance during checkout.',
    description: 'Banner text promoting enhanced protection for US car hire destinations',
  })

  const allBannerText = intl.formatMessage({
    id: 'hpDlHAC3cN4yb7ChX4zZ',
    defaultMessage: 'With our Enhanced Protection you’re refunded for any incurred excess charges. You can select your insurance during checkout.',
    description: 'Banner text promoting enhanced protection for non-US car hire destinations',
  })

  const wantBestCoverLabel = intl.formatMessage({
    id: 'CXjz7gdN94zNot2Rae6y',
    defaultMessage: 'Want the best cover?',
    description: 'Banner heading promoting enhanced protection upgrade for car hire',
  })

  const viewEnhancedProtectionLabel = intl.formatMessage({
    id: 'z4vtyvU-_qA0AL1jJxfK',
    defaultMessage: 'View Enhanced Protection',
    description: 'Button text to view enhanced protection details for car hire',
  })

  const rateOption = offer.rateOptions[0]

  const params: App.CarHireOfferImportantInformationParams = {
    pickUpLocation: {
      code: offer.pickUp.id,
      date: pickUpDate || '',
      time: pickUpTime || '',
    },
    dropOffLocation: {
      code: offer.dropOff.id,
      date: dropOffDate || '',
      time: dropOffTime || '',
    },
    reference: {
      id: rateOption.id,
      idContext: rateOption.idContext,
      type: rateOption.type,
      dateTime: rateOption.dateTime,
      url: rateOption.url,
    },
  }

  const { offerImportantInformation } = useCarHireOfferImportantInformation(params, rateOption.id)
  const hasError = Boolean(offerImportantInformation.error)
  const offerImportantInformationData = offerImportantInformation.data

  return (
    <VerticalSpacer gap={16}>
      <HeadingTextBlock variant="heading6">{whatsCoveredLabel}</HeadingTextBlock>
      <Divider kind="primary" />
      {offerImportantInformation && !!offerImportantInformationData &&
        <>
          <HeadingBlock className="limitedProtectionItemHeading" variant="heading6" startIcon={(<LineCoinsIcon size="M" />)}>{excessFeeLabel}</HeadingBlock>
          <BodyText variant="medium">
            {excessLiabilityText}
          </BodyText>
          <CarInfoList
            offerImportantInformation={offerImportantInformationData}
            hasError={hasError}
            displayCategory="Coverage"
            showTitle={false}
          />
        </>}
      <Divider kind="primary" />
      <HeadingBlock className="limitedProtectionItemHeading" variant="heading6" startIcon={(<LineShieldCheckIcon size="M" />)}>{thirdPartyProtectionLabel}</HeadingBlock>
      <BodyText variant="medium">
        {hasEnhancedProtection && usThirdPartyText}
        {!hasEnhancedProtection && allThirdPartyText}
      </BodyText>
      <BannerWrapper>
        <MessageBanner
          icon={<LineShieldCheckIcon />}
          kind="promotional-primary"
          heading={wantBestCoverLabel}
          secondaryActionButtonProps={{
            children: viewEnhancedProtectionLabel,
            onClick,
          }}
        >
          {hasEnhancedProtection && usBannerText}
          {!hasEnhancedProtection && allBannerText}
        </MessageBanner>
      </BannerWrapper>

    </VerticalSpacer>
  )
}

export default CarHireInsuranceLimitedProtectionInfo
